import {Component, EventEmitter, inject, Input, Output} from '@angular/core';
import {Table} from '@core/components';
import {RiskControlRow} from '@core/types';
import {Confirm} from '@core/modals';
import {ModalService} from '@core/services';

@Component({
  selector: 'core-plan-controls',
  imports: [
    Table
  ],
  templateUrl: './plan-controls.html',
  styleUrl: './plan-controls.scss',
  standalone: true,
  host: {
    style: 'width: 100%;display:flex;flex-direction:column;gap:2rem'
  }
})
export class PlanControls {
  @Input() controls: RiskControlRow[] = [];
  @Input() permanentControls: RiskControlRow[] = [];
  @Input() decisions: Array<{controlId: string | number; decision: 'inherit' | 'promote' | 'finalize'}> = [];
  @Input() updatingControlIds = new Set<string>();
  @Output() decisionChanged = new EventEmitter<{controlId: string | number; decision: 'inherit' | 'promote' | 'finalize'}>();
  @Output() submit = new EventEmitter();
  isSubmittingPhase = false;
  private modal = inject(ModalService);

  decisionOptions(item: RiskControlRow): Array<'inherit' | 'promote' | 'finalize'> {
    if (item.status === 'draft') {
      return ['inherit', 'finalize'];
    }
    return ['inherit', 'promote', 'finalize'];
  }

  selectedDecision(item: RiskControlRow): 'inherit' | 'promote' | 'finalize' {
    const found = this.decisions.find(entry => String(entry.controlId) === String(item.id));
    return (found?.decision || 'inherit') as 'inherit' | 'promote' | 'finalize';
  }

  setDecision(item: RiskControlRow, decision: 'inherit' | 'promote' | 'finalize'): void {
    if (!this.decisionOptions(item).includes(decision)) return;
    this.decisionChanged.emit({controlId: item.id, decision});
  }

  isUpdating(item: RiskControlRow): boolean {
    return this.isSubmittingPhase || this.updatingControlIds.has(String(item.id));
  }

  conceptTags(item: RiskControlRow): string[] {
    const raw = String(item.affects || '').trim();
    if (!raw) return [];
    const normalized = raw.replace(/^Conceptos:\s*/i, '');
    return normalized
      .split(',')
      .map(value => value.trim())
      .filter(value => value !== '');
  }

  statusLabel(item: RiskControlRow): string {
    const status = String(item.status || '').trim().toLowerCase();
    const labels: Record<string, string> = {
      draft: 'Borrador',
      in_execution: 'En ejecución',
      finished: 'Finalizado',
      permanent: 'Permanente',
      discarded: 'Descartado',
    };
    return labels[status] || 'Sin estado';
  }

  async onSubmit() {
    if (this.isSubmittingPhase) return;
    this.isSubmittingPhase = true;
    const summary = this.decisionSummary();
    const first = await this.askForConfirmation(
      'AVISO - SALIR DE FASE 3',
      `Al continuar a la Fase 4 se aplicarán las decisiones tomadas sobre tus controles: ${summary.inherit} heredados, ${summary.promote} promocionados, ${summary.finalize} finalizados. Esto modificará el riesgo residual real del compliance. La acción es irreversible.`,
      'Continuar'
    );
    if (!first) {
      this.isSubmittingPhase = false;
      return;
    }

    const second = await this.askForConfirmation(
      'CONFIRMACIÓN FINAL - FASE 3',
      'Confirma una vez más para aplicar las decisiones. Esta es tu última oportunidad para revisar antes de modificar el riesgo residual del compliance.',
      'Aplicar y continuar'
    );
    if (!second) {
      this.isSubmittingPhase = false;
      return;
    }
    this.submit.emit();
    this.isSubmittingPhase = false;
  }

  private decisionSummary(): {inherit: number; promote: number; finalize: number} {
    const summary = {inherit: 0, promote: 0, finalize: 0};
    for (const control of this.controls) {
      const decision = this.selectedDecision(control);
      if (decision === 'inherit') summary.inherit++;
      if (decision === 'promote') summary.promote++;
      if (decision === 'finalize') summary.finalize++;
    }
    return summary;
  }

  private askForConfirmation(title: string, description: string, confirmText: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.modal.open<Confirm, boolean>(Confirm, {
        inputs: {
          title,
          description,
          confirmText,
          cancelText: 'Cancelar',
        },
        outputs: {
          close: () => resolve(false),
          confirm: () => resolve(true),
        },
        options: {size: 'md', closeOnBackdrop: true},
      });
    });
  }
}
